import { CaretDownIcon } from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { type ReactNode, type SubmitEvent, useMemo, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { logbookClassFor } from '@/features/aircraft/classes';
import type { FlightPurpose, FlightRole, Flight, LogbookEntry } from '@/lib/db';
import { aircraft as aircraftRepo, flights, logbookEntries } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';
import { normaliseRegistration } from '@/lib/parse';

import { capacityHoursForRole } from './caa1373';
import { PURPOSE_LABEL } from './caa-columns';
import { formatIntermediate, isCalendarDate, parseIntermediate } from './columns';
import {
  blankApproaches,
  blankLandings,
  blankTakeoffs,
  blankTimeMatrix,
  recordChanges,
} from './entry';
import { aircraftTypeLabel } from './derived-values';
import { parseDurationHours, roundDecimalHours } from './time';

/**
 * The first of the three layers: enter a flight, fast.
 *
 * This asks for the facts and nothing else — where, what, who, how long, how
 * much of it after dark, what the pilot was doing. Sixteen CAA 1373 hour
 * columns come out the other end without a single one of them being typed,
 * which is the whole argument of the redesign: `caa1373.ts` derives them,
 * `caa-columns.ts` prints them, and a pilot never enters the same hour
 * twice.
 *
 * Progressive disclosure, per §21 of the brief: an ordinary VFR circuit
 * shows nine fields. Training details appear when the purpose is training,
 * instrument time and approaches only when asked for. The grid remains the
 * way to reach any field on any row — this is the fast path, not the only
 * path.
 */

const INPUT_CLASS =
  'border-border bg-surface rounded-control border px-2.5 py-1.5 text-base outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:text-sm';

/**
 * `text-base` above, not `text-sm`: iOS Safari zooms the whole page when a
 * focused input's text is under 16px, and a form that jumps on every tap is
 * unusable one-handed in a cockpit. The `sm:` breakpoint puts it back on a
 * desktop, where the zoom rule does not apply.
 */
function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
      {hint === undefined ? null : (
        <span className="text-muted-foreground text-[0.6875rem]">{hint}</span>
      )}
    </label>
  );
}

/** The roles CAA 1373 has a column for, in the order the form offers them. */
const ROLES: readonly { id: FlightRole; label: string; hint: string }[] = [
  { id: 'pic', label: 'PIC', hint: 'Pilot in command' },
  { id: 'dual', label: 'Dual', hint: 'Receiving instruction' },
  { id: 'student', label: 'Student', hint: 'Receiving instruction, as a student pilot' },
  { id: 'co-pilot', label: 'Co-Pilot', hint: 'Second pilot of a crewed aircraft' },
  { id: 'command-practice', label: 'Command Practice', hint: 'In command under supervision' },
];

const PURPOSES = Object.keys(PURPOSE_LABEL) as FlightPurpose[];

/** Everything the form holds, as strings — parsed once on submit, so a half-typed field is never a half-written entry. */
interface FormState {
  date: string;
  registration: string;
  pic: string;
  otherCrew: string;
  from: string;
  intermediate: string;
  to: string;
  totalTime: string;
  nightTime: string;
  role: FlightRole;
  purpose: FlightPurpose;
  trainingDetails: string;
  actualInstrument: string;
  simulatedInstrument: string;
  groundInstrument: string;
  nonPrecisionApproaches: string;
  precisionApproaches: string;
  takeoffsDay: string;
  takeoffsNight: string;
  landingsDay: string;
  landingsNight: string;
}

function initialState(entry: LogbookEntry | undefined, today: string): FormState {
  if (entry === undefined) {
    return {
      date: today,
      registration: '',
      pic: 'SELF',
      otherCrew: '',
      from: '',
      intermediate: '',
      to: '',
      totalTime: '',
      nightTime: '',
      role: 'pic',
      purpose: 'general',
      trainingDetails: '',
      actualInstrument: '',
      simulatedInstrument: '',
      groundInstrument: '',
      nonPrecisionApproaches: '',
      precisionApproaches: '',
      takeoffsDay: '',
      takeoffsNight: '',
      landingsDay: '',
      landingsNight: '',
    };
  }
  const hours = (value: number) => (value === 0 ? '' : String(value));
  const counts = (value: number) => (value === 0 ? '' : String(value));
  return {
    date: entry.date,
    registration: entry.registration ?? '',
    pic: entry.pic,
    otherCrew: entry.otherCrew ?? '',
    from: entry.from ?? '',
    intermediate: formatIntermediate(entry.intermediate),
    to: entry.to ?? '',
    totalTime: hours(roundDecimalHours(entry.time.dayHr + entry.time.nightHr)),
    nightTime: hours(entry.time.nightHr),
    role: entry.role ?? 'pic',
    purpose: entry.purpose ?? 'general',
    trainingDetails: entry.trainingDetails ?? '',
    actualInstrument: hours(entry.time.actualInstrumentHr),
    simulatedInstrument: hours(entry.time.simulatedInstrumentHr),
    groundInstrument: hours(entry.time.groundInstrumentHr),
    nonPrecisionApproaches: counts(entry.approaches.nonPrecision),
    precisionApproaches: counts(entry.approaches.precision),
    takeoffsDay: counts(entry.takeoffs.day),
    takeoffsNight: counts(entry.takeoffs.night),
    landingsDay: counts(entry.landings.day),
    landingsNight: counts(entry.landings.night),
  };
}

function hoursOrZero(raw: string): number {
  return parseDurationHours(raw) ?? 0;
}

function countOrZero(raw: string): number {
  const parsed = Number.parseInt(raw.trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export interface FlightEntryFormProps {
  /** The entry being edited; omitted to add a new one. */
  entry?: LogbookEntry;
  onDone: (entryId: string) => void;
  onCancel: () => void;
}

export function FlightEntryForm({ entry, onDone, onCancel }: FlightEntryFormProps) {
  const today = formatLocalDate(new Date().toISOString(), { format: 'iso' });
  const [form, setForm] = useState<FormState>(() => initialState(entry, today));
  const [showInstrument, setShowInstrument] = useState(
    () =>
      entry !== undefined &&
      (entry.time.actualInstrumentHr > 0 ||
        entry.time.simulatedInstrumentHr > 0 ||
        entry.time.groundInstrumentHr > 0 ||
        entry.approaches.nonPrecision > 0 ||
        entry.approaches.precision > 0),
  );
  const [error, setError] = useState<string | null>(null);

  const fleet = useLiveQuery(() => aircraftRepo.list(), [], null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  /**
   * The aircraft this registration names, if it is one on the pilot's list.
   *
   * The aircraft database is not this form's to redesign or duplicate (§
   * "Do not modify the aircraft information system"), so the registration is
   * simply looked up: the type and the single/multi-engine class come from
   * the record that already exists, and nothing about the aeroplane is
   * entered here at all.
   */
  const matched = useMemo(() => {
    const registration = normaliseRegistration(form.registration);
    if (registration === '' || fleet === null) {
      return undefined;
    }
    return fleet.find((candidate) => candidate.registration === registration);
  }, [form.registration, fleet]);

  const totalHr = hoursOrZero(form.totalTime);
  const nightHr = hoursOrZero(form.nightTime);
  const dayHr = roundDecimalHours(Math.max(0, totalHr - nightHr));

  const save = async (event: SubmitEvent) => {
    event.preventDefault();
    /*
     * An approved entry is locked here for the same reason it is locked in
     * the grid: `confirmEntry` is what makes it count toward totals and
     * currency, and a field that could still change under that would make
     * the count a moving target. The grid does not offer the form for one,
     * so this is the belt to that braces.
     */
    if (entry !== undefined && !entry.isDraft) {
      setError('This entry is approved. Move it back to draft before editing it.');
      return;
    }
    if (!isCalendarDate(form.date)) {
      setError('Enter the date as YYYY-MM-DD.');
      return;
    }
    if (parseDurationHours(form.totalTime) === null) {
      setError('Flight time takes 1:30, 1.5 or 90m.');
      return;
    }
    if (nightHr > totalHr) {
      setError('Night time cannot be more than the total flight time.');
      return;
    }
    const intermediate = parseIntermediate(form.intermediate.trim());
    if (intermediate === undefined) {
      setError('Intermediate takes aerodrome codes, separated by spaces or commas.');
      return;
    }

    const now = new Date().toISOString();
    const engineClass =
      matched === undefined
        ? (entry?.time.engineClass ?? 'single-engine-land')
        : logbookClassFor(matched);

    /*
     * The one place the role becomes hours. `capacityHoursForRole` puts the
     * whole flight time in one capacity, and the day/night split then falls
     * out of the night figure — which is what makes every 1373 cell a
     * consequence of what was typed on this screen rather than a field of
     * its own.
     */
    const capacity = capacityHoursForRole(form.role, totalHr);

    const base =
      entry ??
      ({
        id: crypto.randomUUID(),
        // A brand-new entry needs a flight to hang off: the flight is the join
        // point every other feature reads through, per `Flight`'s own doc
        // comment, and an entry without one is invisible to the flight list.
        flightId: crypto.randomUUID(),
        date: form.date,
        pic: 'SELF',
        time: blankTimeMatrix(engineClass),
        approaches: blankApproaches(),
        landings: blankLandings(),
        takeoffs: blankTakeoffs(),
        isDraft: true,
        derivedFields: [],
        history: [],
        createdAt: now,
        updatedAt: now,
      } satisfies LogbookEntry);

    if (entry === undefined) {
      const flight: Flight = {
        id: base.flightId,
        date: form.date,
        ...(matched === undefined ? {} : { aircraftId: matched.id }),
        derived: { confidence: 'low' },
        createdAt: now,
        updatedAt: now,
      };
      await flights.put(flight);
    }

    const registration = normaliseRegistration(form.registration);
    const matchedType = matched === undefined ? undefined : aircraftTypeLabel(matched);
    const trainingDetails = form.trainingDetails.trim();
    const otherCrew = form.otherCrew.trim();
    const from = form.from.trim().toUpperCase();
    const to = form.to.trim().toUpperCase();

    const next: LogbookEntry = {
      ...base,
      date: form.date,
      ...(matched === undefined ? {} : { aircraftId: matched.id }),
      ...(registration === '' ? {} : { registration }),
      ...(matchedType === undefined ? {} : { aircraftType: matchedType }),
      pic: form.pic.trim() === '' ? 'SELF' : form.pic.trim(),
      ...(otherCrew === '' ? {} : { otherCrew }),
      ...(from === '' ? {} : { from }),
      ...(to === '' ? {} : { to }),
      ...(intermediate.length === 0 ? {} : { intermediate }),
      role: form.role,
      purpose: form.purpose,
      // Training details belong to a training flight. Kept off any other
      // purpose rather than carried along invisibly, so changing the purpose
      // cannot leave a stale exercise printing in Details of Flight.
      ...(form.purpose === 'training' && trainingDetails !== '' ? { trainingDetails } : {}),
      time: {
        ...base.time,
        engineClass,
        dayHr,
        nightHr,
        ...capacity,
        actualInstrumentHr: hoursOrZero(form.actualInstrument),
        simulatedInstrumentHr: hoursOrZero(form.simulatedInstrument),
        groundInstrumentHr: hoursOrZero(form.groundInstrument),
      },
      approaches: {
        nonPrecision: countOrZero(form.nonPrecisionApproaches),
        precision: countOrZero(form.precisionApproaches),
      },
      takeoffs: {
        day: countOrZero(form.takeoffsDay),
        night: countOrZero(form.takeoffsNight),
      },
      landings: {
        ...base.landings,
        day: countOrZero(form.landingsDay),
        night: countOrZero(form.landingsNight),
      },
      updatedAt: now,
    };

    // A logbook is an official record, so an edit keeps what it replaced —
    // `NZ-LOGBOOK.md` §2.4, the same append-only history a cell edit in the
    // grid leaves behind.
    const saved = entry === undefined ? next : recordChanges(entry, next, now);
    await logbookEntries.put(saved);
    onDone(saved.id);
  };

  return (
    <form
      className="border-border bg-surface rounded-container flex flex-col gap-4 border p-4"
      onSubmit={(event) => {
        void save(event);
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Date">
          <input
            className={INPUT_CLASS}
            type="date"
            value={form.date}
            onChange={(e) => {
              set('date', e.target.value);
            }}
          />
        </Field>
        <Field
          label="Registration"
          hint={
            matched === undefined
              ? 'Matching an aircraft registration fills in its type and class'
              : `${aircraftTypeLabel(matched) ?? matched.registration} — ${logbookClassFor(matched) === 'multi-engine-land' ? 'multi-engine' : 'single-engine'}`
          }
        >
          <input
            className={INPUT_CLASS}
            value={form.registration}
            placeholder="ZK-XXX"
            autoCapitalize="characters"
            onChange={(e) => {
              set('registration', e.target.value.toUpperCase());
            }}
          />
        </Field>
        <Field label="PIC">
          <input
            className={INPUT_CLASS}
            value={form.pic}
            onChange={(e) => {
              set('pic', e.target.value);
            }}
          />
        </Field>
        <Field label="Co-Pilot / Student">
          <input
            className={INPUT_CLASS}
            value={form.otherCrew}
            onChange={(e) => {
              set('otherCrew', e.target.value);
            }}
          />
        </Field>
        <Field label="From">
          <input
            className={INPUT_CLASS}
            value={form.from}
            placeholder="NZAA"
            maxLength={4}
            autoCapitalize="characters"
            onChange={(e) => {
              set('from', e.target.value.toUpperCase());
            }}
          />
        </Field>
        <Field label="Intermediate" hint="Landing points in between, in order">
          <input
            className={INPUT_CLASS}
            value={form.intermediate}
            placeholder="NZNS, NZWP"
            autoCapitalize="characters"
            onChange={(e) => {
              set('intermediate', e.target.value.toUpperCase());
            }}
          />
        </Field>
        <Field label="To">
          <input
            className={INPUT_CLASS}
            value={form.to}
            placeholder="NZAA"
            maxLength={4}
            autoCapitalize="characters"
            onChange={(e) => {
              set('to', e.target.value.toUpperCase());
            }}
          />
        </Field>
        <Field label="Flight time" hint="1:30, 1.5 or 90m">
          <input
            className={INPUT_CLASS}
            value={form.totalTime}
            placeholder="1:15"
            inputMode="decimal"
            onChange={(e) => {
              set('totalTime', e.target.value);
            }}
          />
        </Field>
        <Field
          label="Of which at night"
          hint={
            totalHr === 0
              ? 'Leave blank for a day flight'
              : `Day ${String(dayHr)} · Night ${String(nightHr)}`
          }
        >
          <input
            className={INPUT_CLASS}
            value={form.nightTime}
            placeholder="0"
            inputMode="decimal"
            onChange={(e) => {
              set('nightTime', e.target.value);
            }}
          />
        </Field>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-muted-foreground text-xs">Role</legend>
        <div className="flex flex-wrap gap-1.5">
          {ROLES.map((role) => (
            <Button
              key={role.id}
              type="button"
              size="sm"
              variant={form.role === role.id ? 'outline' : 'ghost'}
              aria-pressed={form.role === role.id}
              title={role.hint}
              onClick={() => {
                set('role', role.id);
              }}
            >
              {role.label}
            </Button>
          ))}
        </div>
        <p className="text-muted-foreground text-[0.6875rem]">
          The role, the aircraft&apos;s class and the night time decide which CAA 1373 column these
          hours print under. Nothing here is entered twice.
        </p>
      </fieldset>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Purpose">
          <select
            className={INPUT_CLASS}
            value={form.purpose}
            onChange={(e) => {
              set('purpose', e.target.value as FlightPurpose);
            }}
          >
            {PURPOSES.map((purpose) => (
              <option key={purpose} value={purpose}>
                {PURPOSE_LABEL[purpose]}
              </option>
            ))}
          </select>
        </Field>
        {/* Only asked for when there is training to describe — §21's progressive disclosure. */}
        {form.purpose === 'training' ? (
          <Field label="Training exercise" hint="Circuits, stalls, EFATO, forced landing…">
            <input
              className={INPUT_CLASS}
              value={form.trainingDetails}
              onChange={(e) => {
                set('trainingDetails', e.target.value);
              }}
            />
          </Field>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Day take-offs">
          <input
            className={INPUT_CLASS}
            value={form.takeoffsDay}
            inputMode="numeric"
            onChange={(e) => {
              set('takeoffsDay', e.target.value);
            }}
          />
        </Field>
        <Field label="Night take-offs">
          <input
            className={INPUT_CLASS}
            value={form.takeoffsNight}
            inputMode="numeric"
            onChange={(e) => {
              set('takeoffsNight', e.target.value);
            }}
          />
        </Field>
        <Field label="Day landings">
          <input
            className={INPUT_CLASS}
            value={form.landingsDay}
            inputMode="numeric"
            onChange={(e) => {
              set('landingsDay', e.target.value);
            }}
          />
        </Field>
        <Field label="Night landings">
          <input
            className={INPUT_CLASS}
            value={form.landingsNight}
            inputMode="numeric"
            onChange={(e) => {
              set('landingsNight', e.target.value);
            }}
          />
        </Field>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="self-start"
          aria-expanded={showInstrument}
          onClick={() => {
            setShowInstrument((open) => !open);
          }}
        >
          <CaretDownIcon
            aria-hidden="true"
            weight={ICON_WEIGHT}
            className={`size-4 transition-transform ${showInstrument ? '' : '-rotate-90'}`}
          />
          Instrument time and approaches
        </Button>
        {showInstrument ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Field label="Actual">
              <input
                className={INPUT_CLASS}
                value={form.actualInstrument}
                inputMode="decimal"
                onChange={(e) => {
                  set('actualInstrument', e.target.value);
                }}
              />
            </Field>
            <Field label="Simulated">
              <input
                className={INPUT_CLASS}
                value={form.simulatedInstrument}
                inputMode="decimal"
                onChange={(e) => {
                  set('simulatedInstrument', e.target.value);
                }}
              />
            </Field>
            <Field label="Ground" hint="Synthetic trainer">
              <input
                className={INPUT_CLASS}
                value={form.groundInstrument}
                inputMode="decimal"
                onChange={(e) => {
                  set('groundInstrument', e.target.value);
                }}
              />
            </Field>
            <Field label="Non-precision approaches">
              <input
                className={INPUT_CLASS}
                value={form.nonPrecisionApproaches}
                inputMode="numeric"
                onChange={(e) => {
                  set('nonPrecisionApproaches', e.target.value);
                }}
              />
            </Field>
            <Field label="Precision approaches">
              <input
                className={INPUT_CLASS}
                value={form.precisionApproaches}
                inputMode="numeric"
                onChange={(e) => {
                  set('precisionApproaches', e.target.value);
                }}
              />
            </Field>
          </div>
        ) : null}
      </div>

      {error === null ? null : (
        <p role="alert" className="text-danger text-xs">
          {error}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm">
          {entry === undefined ? 'Add flight' : 'Save changes'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
